import { c as _c } from "react/compiler-runtime";
import { useState, type FC } from 'react';
import { FloatingCard } from '@components/floating';
import { Button, Flex, Text } from '@endurancewarranty/ui';
import { callScriptPanelAtom } from '@states/callScript/callScriptPanelAtom';
import { useJotaiAtom } from '@states/jotai/hooks';
import type { TCallScriptStep } from '@components/callScript/types';
type Props = Readonly<{
  steps: TCallScriptStep[];
  title: string;
}>;
export const FloatingCallScript: FC<Props> = t0 => {
  const $ = _c(32);
  const {
    steps,
    title
  } = t0;
  const [stepIndex, setStepIndex] = useState(0);
  const [t1, setCallScriptPanelState] = useJotaiAtom(callScriptPanelAtom);
  const {
    isOpen
  } = t1;
  const activeStep = steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;
  if (activeStep == null) {
    return null;
  }
  const hasResponse = activeStep.affirmativeResponse != null || activeStep.negativeResponse != null;
  let t2;
  if ($[0] !== setCallScriptPanelState) {
    t2 = nextIsOpen => setCallScriptPanelState(prev => ({
      ...prev,
      isOpen: nextIsOpen
    }));
    $[0] = setCallScriptPanelState;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <FloatingCard.Header title={title} />;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== activeStep.promptText) {
    t4 = <Text.Body isItalic={true} whitespace="prewrap">{activeStep.promptText}</Text.Body>;
    $[4] = activeStep.promptText;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== hasResponse) {
    t5 = hasResponse && <Text.Body whitespace="prewrap" decoration="underline" fontWeight="bold">Response:</Text.Body>;
    $[6] = hasResponse;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== activeStep.affirmativeResponse) {
    t6 = activeStep.affirmativeResponse != null && <Text.Body color="success">Yes - <em>{activeStep.affirmativeResponse}</em></Text.Body>;
    $[8] = activeStep.affirmativeResponse;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== activeStep.negativeResponse) {
    t7 = activeStep.negativeResponse != null && <Text.Body color="error">No - <em>{activeStep.negativeResponse}</em></Text.Body>;
    $[10] = activeStep.negativeResponse;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isFirstStep) {
    t8 = !isFirstStep && <Button name="previous-script-step" variant="secondary" onClick={() => setStepIndex(_temp)}>Back</Button>;
    $[12] = isFirstStep;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isLastStep || $[15] !== setCallScriptPanelState || $[16] !== steps.length) {
    t9 = isLastStep ? <Button width="full" name="close-script" variant="primary" onClick={() => setCallScriptPanelState(_temp2)}>Done</Button> : <Button width="full" name="continue-script" variant="primary" isDisabled={isLastStep} onClick={() => setStepIndex(prev_2 => Math.min(prev_2 + 1, steps.length - 1))}>Continue</Button>;
    $[14] = isLastStep;
    $[15] = setCallScriptPanelState;
    $[16] = steps.length;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <Flex direction="row" className="mt-3 gap-2">{t8}{t9}</Flex>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t11 = <FloatingCard.Content className="flex flex-col gap-3">{t4}{t5}{t6}{t7}{t10}</FloatingCard.Content>;
    $[21] = t10;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isOpen || $[28] !== t11 || $[29] !== t2 || $[30] !== t3) {
    t12 = <FloatingCard isOpen={isOpen} onOpenChange={t2} pinOnMount={true}>{t3}{t11}</FloatingCard>;
    $[27] = isOpen;
    $[28] = t11;
    $[29] = t2;
    $[30] = t3;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
function _temp(prev_0) {
  return Math.max(prev_0 - 1, 0);
}
function _temp2(prev_1) {
  return {
    ...prev_1,
    isOpen: false
  };
}